import { useEffect, useState } from 'react';

import { useNdaOrgSearch } from '../../hooks/useNda';
import type { NdaOrgSearchResult } from '../../types';

/**
 * CRM org typeahead for the link-edit affordance (PR-2b). A pure picker: it
 * searches `/api/nda/org-search` (governed, acts as the requesting NDA-privileged
 * user) and hands the chosen org to `onPick`; the caller decides which link it
 * sets. `onClear` (optional) lets the operator explicitly unset the link.
 * Inline styles mirror NdaBindModal — no CSS-module coupling.
 */

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(value), ms);
    return () => window.clearTimeout(id);
  }, [value, ms]);
  return debounced;
}

export function NdaOrgPickModal({
  title,
  busy = false,
  onPick,
  onClear,
  onClose,
}: {
  title: string;
  busy?: boolean;
  onPick: (org: NdaOrgSearchResult) => void;
  onClear?: () => void;
  onClose: () => void;
}) {
  const [query, setQuery] = useState('');
  const debounced = useDebounced(query, 200);
  const search = useNdaOrgSearch(debounced);
  const results = search.data?.results ?? [];

  return (
    <div
      style={backdrop}
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div style={modal} role="dialog" aria-label={title}>
        <h3 style={{ marginTop: 0, color: '#333' }}>{title}</h3>
        <p style={{ fontSize: 12, color: '#666', marginTop: 0 }}>
          Search CRM organizations by name. The chosen org is locked to this
          review — the auto-resolver won&rsquo;t overwrite it.
        </p>

        <input
          type="text"
          style={input}
          placeholder="Search organizations…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
          disabled={busy}
        />

        {busy && (
          <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>Saving…</div>
        )}
        {search.isLoading && debounced.trim().length >= 2 && !busy && (
          <div style={{ fontSize: 11, color: '#888', marginTop: 6 }}>Searching…</div>
        )}

        {!busy && debounced.trim().length >= 2 && (
          <div style={resultsBox}>
            {results.length === 0 && !search.isLoading && (
              <div style={{ padding: 10, fontSize: 12, color: '#888' }}>No matches.</div>
            )}
            {results.map((r) => (
              <div key={`org-${r.org_id}`} style={row} onClick={() => onPick(r)}>
                <span><strong>{r.name}</strong></span>
                <span style={{ color: '#888', fontSize: 11 }}>{r.domain ?? `org ${r.org_id}`}</span>
              </div>
            ))}
          </div>
        )}

        <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-between' }}>
          {onClear ? (
            <button type="button" onClick={onClear} style={clearBtn} disabled={busy}>
              Clear link
            </button>
          ) : <span />}
          <button type="button" onClick={onClose} style={cancelBtn} disabled={busy}>
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', display: 'flex',
  alignItems: 'flex-start', justifyContent: 'center', paddingTop: '12vh', zIndex: 1000,
};
const modal: React.CSSProperties = {
  background: '#fff', borderRadius: 8, padding: 20, width: 'min(480px, 92vw)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
};
const input: React.CSSProperties = {
  width: '100%', padding: '8px 10px', fontSize: 14, border: '1px solid #ccc',
  borderRadius: 4, boxSizing: 'border-box',
};
const resultsBox: React.CSSProperties = {
  marginTop: 8, maxHeight: 280, overflowY: 'auto', border: '1px solid #eee', borderRadius: 4,
};
const row: React.CSSProperties = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8,
  padding: '8px 10px', fontSize: 13, cursor: 'pointer', borderBottom: '1px solid #f2f2f2',
};
const cancelBtn: React.CSSProperties = {
  background: '#f2f2f2', border: '1px solid #ddd', borderRadius: 4, padding: '6px 14px',
  fontSize: 13, cursor: 'pointer',
};
const clearBtn: React.CSSProperties = {
  background: '#fff', border: '1px solid #e0b4b4', color: '#a33', borderRadius: 4,
  padding: '6px 14px', fontSize: 13, cursor: 'pointer',
};
